import React, {
  FC, useState
} from 'react';
import {
  useTranslation
} from 'react-i18next';
import {
  Controller, useForm
} from 'react-hook-form';
import {
  zodResolver
} from '@hookform/resolvers/zod';

import {
  Button, Input, successfulToast
} from 'src/shared/components';
import {
  useGetInputValidation
} from 'src/shared/hooks';
import {
  usePatchOrganizationByIdMutation
} from 'src/redux/openapi';
import {
  showWarningFromServer
} from 'src/shared/utils';
import {
  SetupCustomLoginModal
} from 'src/features/Modals';
import {
  PlusRoundedIcon
} from 'src/shared/icons';

import * as Style from './OrganizationSettingsModal.style';
import {
  OrganizationLogoUpload,
  StickyButtonsFooter,
  OrgEmailSettings,
  OrgThemes,
} from './UI';

interface OrgGeneralSettingsTabProps {
  name: string;
  id: string;
  logoUrl: string | null;
  containerHeight: number;
  isOpenModal: boolean;
  closeModalHandler: () => void;
  openModalHandler: () => void;
  customLoginEnabled?: boolean;
  subdomain?: string | null;
  pendingSubdomain?: string | null;
  loginCoverPhotoUrl?: string | null;
}

interface OrgNameForm {
  name: string;
}

export const OrgGeneralSettingsTab: FC<OrgGeneralSettingsTabProps> = ({
  name,
  id,
  logoUrl,
  containerHeight,
  isOpenModal,
  closeModalHandler,
  openModalHandler,
  customLoginEnabled,
  subdomain,
  pendingSubdomain,
  loginCoverPhotoUrl,
}) => {
  const {
    t
  } = useTranslation();

  const [isCustomLoginModalOpen, setIsCustomLoginModalOpen] = useState(false);

  const {
    organizationNameSchema
  } = useGetInputValidation();

  const {
    control, formState, watch, setValue
  } = useForm<OrgNameForm>({
    mode: 'onTouched',
    defaultValues: {
      name,
    },
    resolver: zodResolver(organizationNameSchema),
  });

  const nameValue = watch('name');

  const [changeOrgName, {
    isLoading: isChangeOrgNameLoading
  }] = usePatchOrganizationByIdMutation();

  const handleOrgNameChange = async () => {
    if (nameValue === name || formState.errors.name) {
      return;
    }

    try {
      const resp = await changeOrgName({
        id,
        updateOrganizationBodySchema: {
          name: nameValue,
        },
      }).unwrap();

      successfulToast(resp.message);
    } catch (error) {
      showWarningFromServer(error);
    }
  };

  const handleCancelClick = () => setValue(
    'name',
    name
  );

  return (
    <Style.ModalSettingsTabContainer $height={containerHeight}>
      <OrganizationLogoUpload
        id={id}
        logoSrc={logoUrl}
        name={name}
      />

      <Style.ModalInputRow>
        <Controller
          name="name"
          control={control}
          render={({
            field
          }) => {
            return (
              <Input
                label={t('organisation.organisationName')}
                onChange={field.onChange}
                value={field.value}
                errorMessage={formState.errors.name?.message}
                onBlur={field.onBlur}
              />
            );
          }}
        />
      </Style.ModalInputRow>

      <OrgEmailSettings />

      {customLoginEnabled && (
        <div className="px-6 mb-3">
          <div className="flex justify-between items-center">
            <h3 className="text-22-30 font-bold text-gray7">
              {t('customLogin.setupCustomLogin')}
            </h3>

            <Button
              type="button"
              variant="big-grey-bordered"
              className="w-max !px-4 !py-2.5 text-15-20 font-bold text-gray7"
              onClick={() => setIsCustomLoginModalOpen(true)}
            >
              {t('customLogin.setupLogin')}

              <PlusRoundedIcon />
            </Button>
          </div>

          {pendingSubdomain && (
            <p className="text-15-20 text-dim-gray mt-1">
              {t('customLogin.pendingSubdomain')}

              :
              {' '}

              <strong>{pendingSubdomain}</strong>
            </p>
          )}
        </div>
      )}

      <OrgThemes
        closeModalHandler={closeModalHandler}
        openModalHandler={openModalHandler}
        id={id}
        isOpenModal={isOpenModal}
      />

      {isCustomLoginModalOpen && (
        <SetupCustomLoginModal
          isOpen={isCustomLoginModalOpen}
          onClose={() => setIsCustomLoginModalOpen(false)}
          organizationId={id}
          currentSubdomain={subdomain}
          currentCoverPhotoUrl={loginCoverPhotoUrl}
          pendingSubdomain={pendingSubdomain}
        />
      )}

      {nameValue !== name && (
        <StickyButtonsFooter
          handleCancelClick={handleCancelClick}
          handleOrgNameChange={handleOrgNameChange}
          isChangeOrgNameLoading={isChangeOrgNameLoading}
        />
      )}
    </Style.ModalSettingsTabContainer>
  );
};
